import { Box, Typography, useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Stack, Button } from "@mui/material";

export default function ChannelChangelog({ changelog, channelID, getAccessTokenSilently }) {
    const theme = useTheme();

    if (!changelog || changelog.length === 0) {
        return (
            <Box sx={{ padding: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    No changes have been recorded for this channel yet.
                </Typography>
            </Box>
        );
    }

    const getEventChipColor = (eventName) => {
        switch (eventName) {
            case 'INSERT': return 'success';
            case 'MODIFY': return 'warning';
            case 'REMOVE': return 'error';
            default: return 'default';
        }
    };

    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const date = new Date(timestamp);
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return {
            relative: getRelativeTime(timestamp),
            absolute: date.toLocaleString()
        };
    };

    const handleRevert = async (field, oldValue) => {
        try {
            const accessToken = await getAccessTokenSilently({ authorizationParams: { scope: "write:channels" } });
            
            // Create a minimal update object with just this field
            const updates = { [field]: oldValue };
            
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.int.stationportal.nmp.nonprod-sinclairstoryline.com'}/channel/${channelID}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates),
            });

            if (!response.ok) {
                throw new Error(`Failed to revert: ${response.status}`);
            }

            // Refresh the page to show the updated data
            window.location.reload();
        } catch (error) {
            console.error('Error reverting change:', error);
            alert(`Failed to revert ${field}: ${error.message}`);
        }
    };

    const renderChanges = (changes) => {
        if (!changes || changes.length === 0) return 'N/A';
        
        return (
            <Stack spacing={1}>
                {changes.map((change, index) => (
                    <Box key={index} sx={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {change.field}:
                            </Typography>
                            <br />
                            <Typography variant="caption" color="error.main">
                                {JSON.stringify(change.old_value)} 
                            </Typography>
                            {' → '}
                            <Typography variant="caption" color="success.main">
                                {JSON.stringify(change.new_value)}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleRevert(change.field, change.old_value)}
                            sx={{ 
                                minWidth: 'auto', 
                                fontSize: '10px', 
                                padding: '2px 6px',
                                height: '20px'
                            }}
                        >
                            Revert
                        </Button>
                    </Box>
                ))}
            </Stack>
        );
    };

    return (
        <Box sx={{ width: '100%', height: '100%', padding: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Change History
            </Typography>
            
            <TableContainer 
                component={Paper} 
                sx={{ 
                    maxHeight: '60vh',
                    backgroundColor: theme.palette.background.paper 
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Event</TableCell>
                            <TableCell>Changes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {changelog.map((entry) => {
                            const timeFormatted = formatTimestamp(entry.timestamp);
                            return (
                                <TableRow key={entry.record_id} hover>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {timeFormatted.relative}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {timeFormatted.absolute}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={entry.event_name} 
                                            color={getEventChipColor(entry.event_name)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {renderChanges(entry.changes)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}